/**
 * KeepTix customizer — product page behaviour.
 *
 * The form is built from the layout spec rather than from markup, so labels,
 * character limits and field order all come from the InDesign template by way
 * of shared/ticket-layout.json. Adding or resizing a frame in the template
 * changes this form with no edit here.
 *
 * Colour is a variant. Everything else is a line item property.
 */

// Imported by versioned URL (set by the section's Liquid), never by a bare
// relative path: Shopify's CDN caches an unversioned asset for a year, so the
// layout spec inside the bundle would silently stop updating. The fallback is
// for the local harness, which has no Liquid.
const { spec, ticketSVG, dateLong } = await import(window.KeepTix?.bundleUrl || './keeptix-ticket.js');

const SEATING = ['section', 'row', 'seat'];

class Customizer {
  constructor(root) {
    this.root = root;
    this.shared = {};          // fields common to every ticket in the order
    this.tickets = [{ section: '', row: '', seat: '' }];   // per-ticket seating
    this.touched = [new Set()];  // seating fields the customer typed per ticket
    this.activeTicket = 0;
    this.color = root.querySelector('[name="keeptix-color"]:checked')?.value || 'blue';
    this.tiers = parseTiers(root.dataset.tiers);
    this.basePrice = parseFloat(root.dataset.basePrice) || 0;

    this.el = {
      preview: root.querySelector('[data-keeptix-preview]'),
      previewNote: root.querySelector('[data-keeptix-preview-note]'),
      fields: root.querySelector('[data-keeptix-fields]'),
      qty: root.querySelector('[data-keeptix-qty]'),
      seatMode: root.querySelector('[data-keeptix-seat-mode]'),
      seatGrid: root.querySelector('[data-keeptix-seat-grid]'),
      tiers: root.querySelector('[data-keeptix-tiers]'),
      total: root.querySelector('[data-keeptix-total]'),
      add: root.querySelector('[data-keeptix-add]'),
      error: root.querySelector('[data-keeptix-error]'),
    };

    this.buildFields();
    this.buildSeatGrid();
    this.renderTiers();
    this.bind();
    this.draw();
  }

  /* ---------------------------------------------------------------- build */

  buildFields() {
    const frag = document.createDocumentFragment();
    for (const group of spec.form.groups) {
      if (group.perTicket) continue;              // seating lives in the grid
      const fs = el('fieldset', 'keeptix__group');
      fs.append(el('legend', 'keeptix__legend', group.label));
      for (const key of group.fields) {
        fs.append(group.special === 'datetime' ? this.dateTimeInput(key) : this.textInput(key));
      }
      frag.append(fs);
    }
    this.el.fields.append(frag);
  }

  textInput(key) {
    const field = spec.fields[key];
    const wrap = el('label', 'keeptix__field');
    wrap.append(el('span', 'keeptix__label', field.label));

    const input = document.createElement('input');
    input.type = 'text';
    input.maxLength = field.maxLength;            // hard stop, per the frame width
    input.placeholder = (spec.form.placeholders[key] || '').toUpperCase();   // the ticket prints in capitals
    input.dataset.field = key;
    input.autocomplete = 'off';
    wrap.append(input);

    const count = el('span', 'keeptix__count');
    wrap.append(count);
    if (spec.form.hints[key]) wrap.append(el('span', 'keeptix__hint', spec.form.hints[key]));

    input.addEventListener('input', () => {
      this.shared[key] = input.value;
      count.textContent = `${input.value.length}/${field.maxLength}`;
      count.classList.toggle('is-full', input.value.length >= field.maxLength);
      this.draw();
    });
    count.textContent = `0/${field.maxLength}`;
    return wrap;
  }

  dateTimeInput(key) {
    const wrap = el('label', 'keeptix__field');
    wrap.append(el('span', 'keeptix__label', key === 'date' ? 'Event date' : 'Start time'));
    const input = document.createElement('input');
    input.type = key === 'date' ? 'date' : 'time';
    input.dataset.field = key;
    // No min: most of these commemorate a show the customer already attended,
    // so past dates are the common case, not the exception.
    wrap.append(input);
    if (key === 'time') wrap.append(el('span', 'keeptix__hint', 'Optional'));
    input.addEventListener('input', () => { this.shared[key] = input.value; this.draw(); });
    return wrap;
  }

  buildSeatGrid() {
    this.el.seatGrid.replaceChildren();
    const different = this.seatMode() === 'different';
    const rows = different ? this.qty() : 1;
    this.seatInputs = [];

    for (let i = 0; i < rows; i++) {
      const row = el('div', 'keeptix__seat-row');
      if (different) row.append(el('span', 'keeptix__seat-index', `Ticket ${i + 1}`));
      this.seatInputs[i] = {};
      for (const key of SEATING) {
        const field = spec.fields[key];
        const wrap = el('label', 'keeptix__field keeptix__field--narrow');
        wrap.append(el('span', 'keeptix__label', field.label));
        const input = document.createElement('input');
        input.type = 'text';
        input.maxLength = field.maxLength;
        // Later tickets get no example seat: a grey "24" under ticket 1's real
        // "24" reads as if the seat carried over, which is the one thing it
        // deliberately does not do.
        input.placeholder = i > 0 && key === 'seat' ? '' : (spec.form.placeholders[key] || '').toUpperCase();
        input.value = this.tickets[i]?.[key] || '';
        input.autocomplete = 'off';
        input.addEventListener('focus', () => { this.activeTicket = i; this.draw(); });
        input.addEventListener('input', () => this.setSeating(i, key, input.value));
        wrap.append(input, el('span', 'keeptix__count', `${field.maxLength} max`));
        row.append(wrap);
        this.seatInputs[i][key] = input;
      }
      this.el.seatGrid.append(row);
    }
    this.el.seatGrid.hidden = false;
  }

  /**
   * One seating field changed.
   *
   * Tickets after the first start with ticket 1's section and row, and keep
   * following them until the customer types in that field themselves -- so
   * correcting a typo in ticket 1's section fixes the whole set, but a ticket
   * someone deliberately moved to another section stays where they put it.
   * Seat never follows: it is the field that is expected to differ.
   */
  setSeating(i, key, value) {
    this.tickets[i][key] = value;
    this.activeTicket = i;
    if (i > 0) {
      this.touched[i].add(key);
    } else if (key !== 'seat') {
      for (let j = 1; j < this.tickets.length; j++) {
        if (this.touched[j].has(key)) continue;
        this.tickets[j][key] = value;
        const input = this.seatInputs[j]?.[key];
        if (input) input.value = value;
      }
    }
    this.draw();
  }

  /* ----------------------------------------------------------------- bind */

  bind() {
    this.root.querySelectorAll('[name="keeptix-color"]').forEach(r =>
      r.addEventListener('change', () => { this.color = r.value; this.draw(); }));

    this.root.querySelectorAll('[name="keeptix-seatmode"]').forEach(r =>
      r.addEventListener('change', () => { this.syncTickets(); this.buildSeatGrid(); this.draw(); }));

    this.el.qty.addEventListener('input', () => {
      this.syncTickets();
      this.el.seatMode.hidden = this.qty() < 2;
      this.buildSeatGrid();
      this.draw();
    });

    this.el.add.addEventListener('click', () => this.addToCart());
  }

  /* ---------------------------------------------------------------- state */

  qty() { return Math.max(1, parseInt(this.el.qty.value, 10) || 1); }
  seatMode() { return this.root.querySelector('[name="keeptix-seatmode"]:checked')?.value || 'same'; }

  /**
   * Keep one seating entry per ticket.
   *
   * Sized to the quantity even in "same seat" mode, so flipping to "different"
   * and back does not throw away seats already typed. A new ticket copies
   * ticket 1's section and row but not its seat -- the customer is about to
   * type a different one, and a pre-filled seat would just have to be deleted.
   */
  syncTickets() {
    const n = this.qty();
    const first = this.tickets[0];
    while (this.tickets.length < n) {
      this.tickets.push({ section: first.section || '', row: first.row || '', seat: '' });
      this.touched.push(new Set());
    }
    this.tickets.length = n;
    this.touched.length = n;
    const shown = this.seatMode() === 'different' ? n : 1;
    this.activeTicket = Math.min(this.activeTicket, shown - 1);
  }

  /** Shared fields plus one ticket's seating — what a single ticket shows. */
  valuesFor(i) {
    return { ...this.shared, ...(this.tickets[Math.min(i, this.tickets.length - 1)] || {}) };
  }

  /* ---------------------------------------------------------------- paint */

  draw() {
    const i = this.activeTicket;
    this.el.preview.innerHTML = ticketSVG(spec, this.valuesFor(i), {
      color: this.color,
      width: 1400,
      blankHref: window.KeepTix.blankUrls[this.color],
    });
    this.el.previewNote.textContent =
      this.seatMode() === 'different' && this.qty() > 1 ? `Showing ticket ${i + 1} of ${this.qty()}` : '';
    this.renderTotal();
  }

  renderTiers() {
    this.el.tiers.replaceChildren(...this.tiers.map(t => {
      const chip = el('span', 'keeptix__tier', `${t.qty}+  $${t.price.toFixed(2)} ea`);
      chip.dataset.qty = t.qty;
      return chip;
    }));
  }

  renderTotal() {
    const qty = this.qty();
    const each = priceFor(this.tiers, qty, this.basePrice);
    this.el.tiers.querySelectorAll('.keeptix__tier').forEach(c =>
      c.classList.toggle('is-active', Number(c.dataset.qty) === activeTier(this.tiers, qty)));
    this.el.total.textContent =
      `${qty} ${qty === 1 ? 'ticket' : 'tickets'} — $${each.toFixed(2)} each, $${(each * qty).toFixed(2)} total`;
  }

  /* ------------------------------------------------------------- add/cart */

  /**
   * Identical tickets go in as one line with a quantity; differing seats go in
   * as separate lines. Both shapes expand to one row per physical ticket in
   * the print sheet, so the webhook needs no special case.
   */
  buildItems() {
    const variantId = this.root.querySelector('[name="keeptix-color"]:checked')?.dataset.variantId;
    const group = crypto.randomUUID();
    const qty = this.qty();

    if (this.seatMode() === 'same' || qty === 1) {
      return [{ id: variantId, quantity: qty, properties: this.propertiesFor(0, group) }];
    }
    // Each ticket carries the number the customer gave it (_ticket), so the
    // cart can label it correctly whatever order Shopify lists lines in.
    // Shopify lists the newest line first, so the lines go in last-to-first
    // and ticket 1 ends up at the top, matching the form.
    return this.tickets.map((_, i) => ({
      id: variantId, quantity: 1,
      properties: { ...this.propertiesFor(i, group), _ticket: String(i + 1) },
    })).reverse();
  }

  /**
   * Properties starting with "_" are hidden from the customer but still reach
   * the order. The raw date and time go in hidden so the webhook can derive
   * Short Date and Date & Time with shared/format.js rather than parsing a
   * display string back apart.
   */
  propertiesFor(i, group) {
    const v = this.valuesFor(i);
    const props = {};
    for (const [key, field] of Object.entries(spec.fields)) {
      if (field.derived) continue;
      if (v[key]) props[field.label] = v[key].toUpperCase();
    }
    if (v.date) props['Date'] = dateLong(v.date);
    props._date = v.date || '';
    props._time = v.time || '';
    props._group = group;
    return props;
  }

  async addToCart() {
    this.el.error.hidden = true;
    const missing = this.validate();
    if (missing.length) {
      this.el.error.textContent = `Please fill in: ${missing.join(', ')}`;
      this.el.error.hidden = false;
      return;
    }
    this.el.add.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: this.buildItems() }),
      });
      if (!res.ok) throw new Error((await res.json()).description || 'Could not add to cart');
      window.location.href = '/cart';
    } catch (err) {
      this.el.error.textContent = err.message;
      this.el.error.hidden = false;
      this.el.add.disabled = false;
    }
  }

  validate() {
    const required = ['artist', 'venue'];
    const missing = required.filter(k => !this.shared[k]).map(k => spec.fields[k].label);
    if (!this.shared.date) missing.push('Event date');

    // Extra tickets start with an empty seat on purpose, so a forgotten one
    // would otherwise print blank. Only enforced when seats are meant to differ.
    if (this.seatMode() === 'different' && this.qty() > 1) {
      const blank = this.tickets.map((t, i) => (t.seat ? null : i + 1)).filter(Boolean);
      if (blank.length) missing.push(`a seat for ticket ${blank.join(', ')}`);
    }
    return missing;
  }
}

/* ------------------------------------------------------------------ utils */

function el(tag, className, text) {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text != null) n.textContent = text;
  return n;
}

/** "1:15\n2:12\n4:10" -> [{qty,price}], highest quantity last. */
function parseTiers(raw) {
  return String(raw || '')
    .split('\n')
    .map(l => l.trim()).filter(Boolean)
    .map(l => { const [q, p] = l.split(':'); return { qty: parseInt(q, 10), price: parseFloat(p) }; })
    .filter(t => Number.isFinite(t.qty) && Number.isFinite(t.price))
    .sort((a, b) => a.qty - b.qty);
}

function activeTier(tiers, qty) {
  let hit = null;
  for (const t of tiers) if (qty >= t.qty) hit = t.qty;
  return hit;
}

function priceFor(tiers, qty, fallback) {
  let price = fallback;
  for (const t of tiers) if (qty >= t.qty) price = t.price;
  return price;
}

document.querySelectorAll('[data-keeptix-customizer]').forEach(n => new Customizer(n));
